<template>
  <div class="bg-white rounded">
    <spinner v-if="loading"></spinner>

    <div v-else>
      <empty-list-view v-if="areIntegrationsEmpty"
                       title="Connect your first integration to Amio!"
                       :description="emptyViewDescription">
        <add-new-integration-link
          slot="button-add"
          :channel-id="channelId"></add-new-integration-link>
      </empty-list-view>

      <div v-else>
        <add-new-integration-link :channel-id="channelId"></add-new-integration-link>

        <table class="table table-hover table-responsive-md">
          <thead>
          <tr>
            <th></th>
            <th>Name</th>
            <th>Integration Type</th>
            <th>Channel</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="integration in integrations" :key="integration.id">
            <td>
              <integration-icon :type="integration.type" class="rounded-circle w-25-p" width="25px"></integration-icon>
            </td>
            <td>
              <router-link :to="linkToDetail(integration)">
                {{integration.name}}
              </router-link>
            </td>
            <td>{{integration.type | integrationType}}</td>
            <td class="comma-separated-list">
              <span v-for="channel in integration.channels.slice(0, MAX_CHANNELS)"
                    :key="channel.id">
                <router-link :to="{ path: `/administration/channels/${channel.id}/info` }">
                  {{channel.name}}</router-link>
              </span>
              <small v-if="integration.channels.length > MAX_CHANNELS" class="text-muted"> and {{integration.channels.length - MAX_CHANNELS}} more</small>
            </td>
            <td class="text-right">
              <router-link role="button" class="btn btn-outline-primary btn-sm mr-2"
                           :to="linkToDetail(integration)">
                Detail
              </router-link>
              <a href="#" role="button" @click="deleteIntegration(integration)">
                <icon class="btn-icon text-danger" name="trash" scale="1.0"/>
              </a>
            </td>
          </tr>
          </tbody>
        </table>

        <b-pagination v-model="pagination.currentPage"
                      :total-rows="pagination.totalItems"
                      :per-page="pagination.listMaxSize"
                      :hide-goto-end-buttons="true"
                      @change="page => pagination_pageChanged(getIntegrations)(page)">
        </b-pagination>
      </div>

      <modal-confirm-delete
        @integration-deleted="onIntegrationDeleted"
        :showModal="showDeleteModal"
        :integration="integrationForDeletion">
      </modal-confirm-delete>
    </div>
  </div>
</template>

<script>
  import channelService from '@/services/amio-api/channel.service'
  import IntegrationIcon from '@/components/administration/integration/detail/IntegrationIcon'
  import integrationService from '@/services/amio-api/integration.service'
  import EmptyListView from '@/components/administration/common/EmptyListView'
  import isEmpty from 'ramda/src/isEmpty'
  import ConfirmDelete from '@/components/administration/integration/common/ConfirmDeleteIntegration'
  import AddNewIntegrationLink from '@/components/administration/integration/common/AddNewIntegrationLink'
  import paginationMixin from '@/mixins/pagination.mixin'
  import 'vue-awesome/icons/trash'
  import 'vue-awesome/icons/arrow-left'
  import loadingMixin from '@/mixins/loading.mixin'

  const MAX_CHANNELS = 3

  export default {
    mixins: [
      paginationMixin,
      loadingMixin
    ],

    components: {
      'add-new-integration-link': AddNewIntegrationLink,
      'empty-list-view': EmptyListView,
      'integration-icon': IntegrationIcon,
      'modal-confirm-delete': ConfirmDelete
    },

    store: {
      showDeleteModal: 'administration.integrationDetail.showDeleteModal'
    },

    data() {
      return {
        MAX_CHANNELS,
        channelId: null,
        emptyViewDescription: `
                 Integrations add new features like analytics or NLP to your channels.
                 See <a href="https://docs.amio.io/v1.0/docs/integrations">documentation</a> for more detail.
        `,
        integrations: [],
        integrationForDeletion: {}
      }
    },

    computed: {
      areIntegrationsEmpty() {
        return isEmpty(this.integrations)
      }
    },

    methods: {
      deleteIntegration(integration) {
        this.integrationForDeletion = integration
        this.showDeleteModal = true
      },

      linkToDetail(integration) {
        return {
          name: 'administration.integrations.detail',
          params: {id: integration.id}
        }
      },

      onIntegrationDeleted() {
        this.getIntegrations()
          .then(() => {
            this.pagination_updatePage(this.getIntegrations, this.integrations)
          })
      },

      getIntegrations() {
        this.loading = true
        const paginationParams = this.pagination_createParams()

        const fetchIntegrationsPromise = this.channelId
          ? channelService.getIntegrations(this.channelId, paginationParams)
          : integrationService.list(paginationParams)

        fetchIntegrationsPromise.then(({items, totalItems}) => {
          this.integrations = items
          this.pagination_setTotalItems(totalItems)
          this.loading = false
        })
      }
    },

    mounted() {
      this.channelId = this.$route.params.id
      this.getIntegrations()
    },

    beforeRouteUpdate(to, from, next) {
      if (to.query.page === this.pagination_getCurrentPage()) return next()

      this.pagination_setCurrentPage(to.query.page || 1)
      next(this.getIntegrations)
    }
  }
</script>

<style>

  td img {
    /* hack for Safari - the rounded icon is rendered weirdly without it */
    display: block;
  }
</style>
